import React, { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    TICKET_STATUS,
    TICKET_TYPE,
    ORDER_STATUS,
    USAGE_TICKET_STATUS,
    REMAINING_ORDER_DISTRIBUTION_STATUS,
} from '../../utils/Constants';
import {
    canActivateNow,
    canDisplayNow,
    isAfterActivateDeadline,
    isAfterExpired,
    isRefunded,
    isRefundPending,
    isReservationCanceled,
    isReserveTicket,
} from '../../utils';
import useActivateTicket from '../../hooks/useActivateTicket';
import useUsageSpotTicket from '../../hooks/useUsageSpotTicket';

import icNext from '../../assets/imgs/ic_top.svg?react';
import CenterLoading from '../CenterLoading/CenterLoading';

import styles from './styles.module.css';
import CustomAntButton from '../CustomAntButton/CustomAntButton';
import { OrderTicket, OrderTicketList } from 'types';

const BUTTON_TYPE = { LIST: 'list', DETAIL: 'detail' };

type UseTicketButtonProps = {
    buttonType?: string;
    ticket?: OrderTicket | OrderTicketList;
};

const UseTicketButton = memo(({ buttonType = BUTTON_TYPE.LIST, ticket: orderTicket }: UseTicketButtonProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { loading: isCheckingActivate, checkActivateTicket, ActivateConfirmModal } = useActivateTicket(); // 通常のチケット使用
    const { loading: isCheckingSpotTicket, checkUsageSpotTicket } = useUsageSpotTicket(); // スポットコードによるチケット使用

    /**
     * チケット詳細画面へ遷移する
     */
    const toDetail = useCallback(() => {
        navigate('/bought/detail', {
            state: {
                orderId: orderTicket?.orderId,
                orderTicketId: orderTicket?.orderTicketId ?? null,
            },
        });
    }, [navigate, orderTicket]);

    /**
     * チケット表示画面へ遷移する
     */
    const toTicket = useCallback(() => {
        navigate('/bought/ticket', {
            state: {
                orderId: orderTicket?.orderId,
                orderTicketId: orderTicket?.orderTicketId ?? null,
            },
        });
    }, [navigate, orderTicket]);

    const { disable, value, buttonAction } = useMemo(() => {
        if (!orderTicket) {
            return {
                disable: false,
                value: '',
                buttonAction: () => {},
            };
        }

        const { status, ticketType, orderUsageTickets, activateDeadlineAt, orderStatus, remainingOrderDistribution } =
            orderTicket;
        const enableNow = canActivateNow(orderTicket);

        if (isRefunded(orderTicket)) {
            return { disable: true, value: t('button.refunded'), buttonAction: toDetail };
        } else if (isRefundPending(orderTicket)) {
            return { disable: true, value: t('button.refunding'), buttonAction: toDetail };
        } else if (status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED) {
            return { disable: true, value: t('button.expired'), buttonAction: toDetail };
        } else if (
            status === TICKET_STATUS.BOUGHT &&
            orderStatus !== ORDER_STATUS.USER_REFUND_ENABLED &&
            orderStatus !== ORDER_STATUS.STOCK_USER_REFUND_ENABLED
        ) {
            //予約チケット
            if (isReserveTicket(orderTicket)) {
                if (isReservationCanceled(orderTicket as OrderTicket)) {
                    return { disable: true, value: t('button.canceled'), buttonAction: toDetail };
                }
            }
            //企画券場合は
            if (ticketType === TICKET_TYPE.TICKET) {
                const { status: orderUsageTicketStatus, expiredAt } = orderUsageTickets?.[0] ?? {};
                const { remainingOrderDistributionStatus } = remainingOrderDistribution ?? {};

                // 分配残数情報をチェック
                if (remainingOrderDistributionStatus === REMAINING_ORDER_DISTRIBUTION_STATUS.USED) {
                    return { disable: true, value: t('button.used'), buttonAction: toDetail };
                }

                if (
                    remainingOrderDistributionStatus === REMAINING_ORDER_DISTRIBUTION_STATUS.BOUGHT ||
                    remainingOrderDistributionStatus === REMAINING_ORDER_DISTRIBUTION_STATUS.ALL_DISTRIBUTED
                ) {
                    if (isAfterActivateDeadline(activateDeadlineAt)) {
                        return { disable: true, value: t('button.expired'), buttonAction: toDetail };
                    } else if (enableNow) {
                        return {
                            disable: false,
                            value: t('button.activate-ticket'),
                            buttonAction: () => void checkActivateTicket(orderTicket),
                        };
                    }
                }
                if (orderUsageTicketStatus === USAGE_TICKET_STATUS.BOUGHT) {
                    if (isAfterActivateDeadline(activateDeadlineAt)) {
                        return { disable: true, value: t('button.expired'), buttonAction: toDetail };
                    } else {
                        if (enableNow) {
                            return {
                                disable: false,
                                value: t('button.activate-ticket'),
                                buttonAction: () => void checkActivateTicket(orderTicket),
                            };
                        } else {
                            return { disable: true, value: t('button.out-of-available'), buttonAction: toDetail };
                        }
                    }
                } else if (orderUsageTicketStatus === USAGE_TICKET_STATUS.ACTIVATED) {
                    if (isAfterExpired(expiredAt)) {
                        return { disable: true, value: t('button.used'), buttonAction: toDetail };
                    } else {
                        const displayNow = canDisplayNow(orderTicket);
                        if (displayNow) {
                            return {
                                disable: false,
                                value: t('button.view-ticket'),
                                buttonAction: toTicket,
                            };
                        } else {
                            return { disable: true, value: t('button.out-of-available'), buttonAction: toDetail };
                        }
                    }
                } else if (
                    orderUsageTicketStatus === USAGE_TICKET_STATUS.EXPIRED ||
                    orderUsageTicketStatus === USAGE_TICKET_STATUS.USED
                ) {
                    return { disable: true, value: t('button.used'), buttonAction: toDetail };
                } else {
                    return { disable: true, value: t('button.ticket-not-available'), buttonAction: toDetail };
                }
            } else if (ticketType === TICKET_TYPE.COUPON) {
                //回数券場合は
                const hasEveryUsed = orderUsageTickets?.every(({ status }) => status === USAGE_TICKET_STATUS.USED);
                if (hasEveryUsed) return { disable: true, value: t('button.used'), buttonAction: toDetail };
                if (isAfterActivateDeadline(activateDeadlineAt)) {
                    return { disable: true, value: t('button.expired'), buttonAction: toDetail };
                } else {
                    if (enableNow) {
                        return {
                            disable: false,
                            value: t('button.activate-ticket'),
                            buttonAction: () => void checkUsageSpotTicket(orderTicket, true),
                        };
                    } else {
                        return { disable: true, value: t('button.out-of-available'), buttonAction: toDetail };
                    }
                }
            }
        } else {
            return { disable: true, value: t('button.ticket-not-available'), buttonAction: toDetail };
        }
    }, [orderTicket, checkActivateTicket, checkUsageSpotTicket, t, toDetail, toTicket]) ?? {
        disable: false,
        value: '',
        buttonAction: () => {},
    };

    return (
        <>
            {(isCheckingSpotTicket || isCheckingActivate) && <CenterLoading />}
            <ActivateConfirmModal />
            {buttonType === BUTTON_TYPE.LIST && (
                <div className={`${styles.button} ${disable ? styles.disable : ''}`} onClick={buttonAction}>
                    <p className={`${styles.text} ${disable ? styles.disable : ''}`}>{value}</p>
                    <div className={styles.layout}>
                        {React.createElement(icNext, {
                            className: `${styles.icon}`,
                        })}
                    </div>
                </div>
            )}
            {buttonType === BUTTON_TYPE.DETAIL && (
                <CustomAntButton
                    type={disable ? 'disabled' : 'primary'}
                    className={styles.ticketUsageBtn}
                    block
                    disabled={disable}
                    onClick={buttonAction}
                >
                    {value}
                </CustomAntButton>
            )}
        </>
    );
});

export default UseTicketButton;
